import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { companyApi } from "@/services/api";
import { useAuthStore } from "@/stores/auth.store";
import {
  Search,
  Users,
  MoreHorizontal,
  Loader2,
  X,
  Mail,
  Pencil,
  UserCheck,
  UserX,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import {
  companyInviteUserSchema,
  updateUserRoleSchema,
  type CompanyInviteUserInput,
  type UpdateUserRoleInput,
  type UserRoleType,
} from "@/lib/validations";

interface User {
  id: string;
  email: string;
  name: string;
  role: UserRoleType;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  loginCount?: number;
}

const roleConfig = {
  ADMIN: { label: "Admin", variant: "default" as const },
  OPERATOR: { label: "Operador", variant: "secondary" as const },
  VIEWER: { label: "Visualizador", variant: "outline" as const },
};

const roleOptions = [
  { value: "ADMIN" as const, label: "Admin", description: "Acesso total e pode gerenciar equipe" },
  { value: "OPERATOR" as const, label: "Operador", description: "Pode fazer operações" },
  { value: "VIEWER" as const, label: "Visualizador", description: "Apenas leitura" },
];

export function CompanyUsersPage() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // API error states
  const [inviteApiError, setInviteApiError] = useState("");
  const [editApiError, setEditApiError] = useState("");

  // Confirm dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  // Invite form with Zod validation
  const {
    register: registerInvite,
    handleSubmit: handleSubmitInvite,
    reset: resetInviteForm,
    watch: watchInvite,
    setValue: setInviteValue,
    formState: { errors: inviteErrors, isSubmitting: isInviting, isValid: isInviteValid },
  } = useForm<CompanyInviteUserInput>({
    resolver: zodResolver(companyInviteUserSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      name: "",
      role: "VIEWER",
    },
  });

  // Edit role form with Zod validation
  const {
    setValue: setEditValue,
    watch: watchEdit,
    handleSubmit: handleSubmitEdit,
    formState: { isSubmitting: isUpdating },
  } = useForm<UpdateUserRoleInput>({
    resolver: zodResolver(updateUserRoleSchema),
    defaultValues: {
      role: "VIEWER",
    },
  });

  const selectedInviteRole = watchInvite("role");
  const selectedEditRole = watchEdit("role");

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const data = await companyApi.users.list();
      setUsers(data);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    } finally {
      setLoading(false);
    }
  }

  async function onInviteSubmit(data: CompanyInviteUserInput) {
    setInviteApiError("");

    try {
      await companyApi.users.invite(data);
      resetInviteForm();
      setShowInviteModal(false);
      await loadUsers();
    } catch (error: any) {
      setInviteApiError(error.message || "Erro ao convidar usuário");
    }
  }

  async function onEditSubmit(data: UpdateUserRoleInput) {
    if (!selectedUser) return;
    setEditApiError("");

    try {
      await companyApi.users.updateRole(selectedUser.id, data.role);
      setShowEditModal(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (error: any) {
      setEditApiError(error.message || "Erro ao atualizar usuário");
    }
  }

  function openConfirmDialog(
    title: string,
    description: string,
    confirmText: string,
    variant: "default" | "danger",
    onConfirm: () => Promise<void>
  ) {
    setConfirmDialog({
      open: true,
      title,
      description,
      confirmText,
      variant,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await onConfirm();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
        } catch (error) {
          console.error("Erro na operação:", error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false,
    });
  }

  function closeConfirmDialog() {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }

  function handleToggleActive(user: User) {
    const isActive = user.isActive;
    openConfirmDialog(
      isActive ? "Desativar Membro" : "Ativar Membro",
      isActive
        ? `Tem certeza que deseja desativar ${user.name}? Ele não poderá mais acessar o sistema.`
        : `Tem certeza que deseja ativar ${user.name}? Ele voltará a ter acesso ao sistema.`,
      isActive ? "Desativar" : "Ativar",
      isActive ? "danger" : "default",
      async () => {
        await companyApi.users.toggleActive(user.id);
        await loadUsers();
      }
    );
  }

  function openEditModal(user: User) {
    setSelectedUser(user);
    setEditValue("role", user.role);
    setEditApiError("");
    setShowEditModal(true);
  }

  function openInviteModal() {
    resetInviteForm();
    setInviteApiError("");
    setShowInviteModal(true);
  }

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  const activeUsers = users.filter((u) => u.isActive).length;
  const admins = users.filter((u) => u.role === "ADMIN").length;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Users className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Equipe"
        description="Gerencie os membros da sua empresa"
        stats={[
          { value: users.length, label: "Total" },
          { value: activeUsers, label: "Ativos" },
          { value: admins, label: "Admins" },
        ]}
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={openInviteModal}
          >
            <Mail className="w-4 h-4 mr-2" />
            Convidar Membro
          </Button>
        }
      />

      {/* Actions Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Carregando...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum membro encontrado</p>
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-4 font-medium">Membro</th>
                        <th className="p-4 font-medium">Função</th>
                        <th className="p-4 font-medium">Status</th>
                        <th className="p-4 font-medium">Acessos</th>
                        <th className="p-4 font-medium">Último acesso</th>
                        <th className="p-4 font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="border-b border-border last:border-0 hover:bg-muted/50"
                        >
                          <td className="p-4">
                            <div>
                              <p className="font-medium">
                                {user.name}
                                {user.id === currentUser?.id && (
                                  <span className="ml-2 text-xs text-muted-foreground">(você)</span>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge variant={roleConfig[user.role].variant}>
                              {roleConfig[user.role].label}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge variant={user.isActive ? "success" : "secondary"}>
                              {user.isActive ? "Ativo" : "Inativo"}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {user.loginCount ?? 0}
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Nunca"}
                          </td>
                          <td className="p-4">
                            {user.id !== currentUser?.id ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[160px]">
                                  <DropdownMenuItem onClick={() => openEditModal(user)}>
                                    <Pencil className="w-3 h-3 mr-2" />
                                    Editar Função
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                                    {user.isActive ? (
                                      <>
                                        <UserX className="w-3 h-3 mr-2" />
                                        Desativar
                                      </>
                                    ) : (
                                      <>
                                        <UserCheck className="w-3 h-3 mr-2" />
                                        Ativar
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-border">
                  {filteredUsers.map((user) => (
                    <div key={user.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">
                            {user.name}
                            {user.id === currentUser?.id && (
                              <span className="ml-2 text-xs text-muted-foreground">(você)</span>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                        </div>
                        {user.id !== currentUser?.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[160px]">
                              <DropdownMenuItem onClick={() => openEditModal(user)}>
                                <Pencil className="w-3 h-3 mr-2" />
                                Editar Função
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                                {user.isActive ? (
                                  <>
                                    <UserX className="w-3 h-3 mr-2" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="w-3 h-3 mr-2" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={roleConfig[user.role].variant}>
                          {roleConfig[user.role].label}
                        </Badge>
                        <Badge variant={user.isActive ? "success" : "secondary"}>
                          {user.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{user.loginCount ?? 0} acessos</span>
                        <span>
                          Último: {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Nunca"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
        </CardContent>
      </Card>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Convidar Membro</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInviteModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitInvite(onInviteSubmit)} className="space-y-4">
                {inviteApiError && (
                  <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                    {inviteApiError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email *</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="usuario@empresa.com"
                    {...registerInvite("email")}
                  />
                  {inviteErrors.email && (
                    <p className="text-sm text-destructive">{inviteErrors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invite-name">Nome *</Label>
                  <Input
                    id="invite-name"
                    placeholder="Nome completo"
                    {...registerInvite("name")}
                  />
                  {inviteErrors.name && (
                    <p className="text-sm text-destructive">{inviteErrors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Função *</Label>
                  <div className="space-y-2">
                    {roleOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-start p-3 border rounded-md cursor-pointer ${
                          selectedInviteRole === option.value
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <input
                          type="radio"
                          value={option.value}
                          checked={selectedInviteRole === option.value}
                          onChange={(e) => setInviteValue("role", e.target.value as UserRoleType)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <p className="font-medium">{option.label}</p>
                          <p className="text-sm text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
                  Uma senha temporária será enviada por email para o novo membro.
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowInviteModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={!isInviteValid || isInviting}>
                    {isInviting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Enviar Convite
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Editar Função</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitEdit(onEditSubmit)} className="space-y-4">
                {editApiError && (
                  <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                    {editApiError}
                  </div>
                )}

                <div className="p-3 bg-muted rounded-md">
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>

                <div className="space-y-2">
                  <Label>Nova Função</Label>
                  <div className="space-y-2">
                    {roleOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-start p-3 border rounded-md cursor-pointer ${
                          selectedEditRole === option.value
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <input
                          type="radio"
                          value={option.value}
                          checked={selectedEditRole === option.value}
                          onChange={(e) => setEditValue("role", e.target.value as UserRoleType)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <p className="font-medium">{option.label}</p>
                          <p className="text-sm text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isUpdating}>
                    {isUpdating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Salvar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />
    </div>
  );
}
